import { type CARD_TYPE_TO_ORGANIZATION } from '@constants'

export enum PaymentMethodType {
  /** 信用卡一次付清 */
  CREDIT_CARD_ONCE = 'credit_card_once',
  /** 信用卡分期付款 */
  CREDIT_CARD_INSTALLMENT = 'credit_card_installment',
  JKO_PAY = 'jko_pay',
  LINE_PAY = 'line_pay',
  APPLE_PAY = 'apple_pay',
  /** 貨到付款 */
  CASH_ON_DELIVERY = 'cash_on_delivery',
  /** 超商代碼 */
  CVS = 'cvs',
  /** ATM轉帳 */
  ATM = 'atm',
  /** 指定付款 */
  ASSIGNED_PAYMENT = 'assigned_payment',
  /** 指定付款（開發票） */
  ASSIGNED_PAYMENT_INVOICE = 'assigned_payment_invoice',
  /** 線下付款 */
  OFFLINE_PAYMENT = 'offline_payment',
  ALIPAY_HK = 'alipay_hk',
  WECHAT_PAY = 'wechat_pay',
  PAYME = 'payme',
}

export enum PaymentServiceType {
  TAPPAY = 'tappay',
  ECPAY = 'ecpay',
  CHECKOUTPAY = 'checkoutpay',
  LINEPAY = 'linepay',
  APPLEPAY = 'applepay',
  PAYME = 'payme',
  FIRSTBANK = 'firstbank',
}

export const PaymentMethodMap: Record<PaymentMethodType, string> = {
  [PaymentMethodType.CASH_ON_DELIVERY]: '貨到付款',
  [PaymentMethodType.ASSIGNED_PAYMENT_INVOICE]: '指定付款（開發票）',
  [PaymentMethodType.ASSIGNED_PAYMENT]: '指定付款 (不開發票)',
  [PaymentMethodType.CREDIT_CARD_ONCE]: '信用卡 一次付清',
  [PaymentMethodType.CREDIT_CARD_INSTALLMENT]: '信用卡 (Credit/Debit Card)',
  [PaymentMethodType.JKO_PAY]: '街口支付',
  [PaymentMethodType.LINE_PAY]: 'LINE Pay',
  [PaymentMethodType.APPLE_PAY]: 'Apple Pay',
  [PaymentMethodType.ATM]: 'ATM 轉帳',
  [PaymentMethodType.CVS]: '超商代碼繳費',
  [PaymentMethodType.OFFLINE_PAYMENT]: '現場付款 (需排隊)',
  [PaymentMethodType.ALIPAY_HK]: 'AlipayHK',
  [PaymentMethodType.WECHAT_PAY]: 'WeChat Pay',
  [PaymentMethodType.PAYME]: 'PayMe',
}

export interface AtmBank {
  name: string
  enabled: boolean
  bank_code: string
}

export interface AtmMetas {
  deadline: string | number
  banks: AtmBank[]
}

/**
 * 新版付款方式
 */
export interface PaymentMethod {
  /**
   * 付款方式 ID
   */
  id: number
  /**
   * 付款方式類型
   */
  type: PaymentMethodType
  /**
   * 付款服務商
   */
  service_type: PaymentServiceType | null
  /**
   * 付款方式名稱
   */
  name: string
  /**
   * 付款方式描述
   */
  description: string
  /**
   * 付款方式額外設定，目前用於 ATM 銀行清單
   */
  metas?: AtmMetas
}

/**
 * wp my-account中設定信用卡資訊
 */
export interface UserPaymentTokenInfo {
  payment_token_id: number
  payment_token_card_no: string
  card_type?: string
  expire_month?: string
  expire_year?: string

  /** Last 4 digits of the card number */
  last4: string // e.g. "1234"
  /** Cardholder information */
  cardholder: Cardholder
  issuer_info?: IssuerInfo
}

export interface IssuerInfo {
  bank_id: string
  issuer: string
  issuer_zh_tw: string
}
export interface Cardholder {
  /**
     * 信用卡面持卡人英文姓名。符號僅支援「-」、「.」及「,」。
     * Max 45 chars. Pattern: ^[a-zA-Z0-9\s\-\.,]+$
     */
  name_en: string // e.g. "WANG XIAO MING"

  /**
     * 持卡人電子信箱，必須符合 RFC 5322 規範。
     * Max 40 chars.
     */
  email: string // e.g. "user@example.com"

  /**
     * 持卡人電話號碼（E.164 digits-only; no '+'). Max 16 chars. Pattern: ^\d+$
     * Example provided: "0912345678"
     */
  phone_number: string

  /**
     * 電話號碼國碼（digits-only; no '+'). Max 3 chars. Pattern: ^\d+$
     * Example: "886"
     */
  phone_number_country_code: string
}

export interface WpUserPaymentToken {
  /** Unique identifier for the payment token */
  payment_token_id: number // e.g. 123

  /** Payment gateway identifier */
  gateway_id: string // e.g. "dni-tappay"

  /** Card type (VISA, MASTERCARD, etc.) */
  card_type: keyof typeof CARD_TYPE_TO_ORGANIZATION // e.g. "VISA"

  /** Last 4 digits of the card number */
  last4: string // e.g. "1234"

  /** Card expiry year */
  expiry_year: string // e.g. "2026"

  /** Card expiry month */
  expiry_month: string // e.g. "12"

  /** Whether this is the default payment token */
  is_default: boolean // e.g. false

  /** Bank name of the card issuer */
  issuer_info: {
    issuer: string
    issuer_zh_tw: string
    bank_id: string
  }

  /** Cardholder information */
  cardholder: Cardholder
}

/** 用於處理 記憶卡號 結帳頁持卡人資訊輸入 */
export type CardholderInfo = Cardholder & {
  token: number | null
}
